import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid, Typography, useTheme, alpha } from '@mui/material';
import StraightenIcon from '@mui/icons-material/Straighten';
import ScaleIcon from '@mui/icons-material/Scale';
import { BoxEditType, BoxType, GetRatesContextType } from '../../Type';
import SbTextField from '../../../../components/Textfield';
import { boxSchema } from './Schema';
import GetRatesContext from '../../../../context/GetRates';
import { ValidationResult } from 'joi';
import { LENGTH_UNITS } from '../../Constant';
import Actions from '../Actions';
import { calculateVolume } from '../../../../services/calculateVolume';
import { useAiValidation } from '../../../../hooks/useAiValidation';
import PackageAiValidationAlert from '../../../../components/PackageAiValidationAlert';

function BoxEdit({ setBoxState, data }: BoxEditType) {
  const theme = useTheme();
  const { boxes, setBoxes, ratesInput } = React.useContext(GetRatesContext) as GetRatesContextType;
  const [initials, setInitials] = useState<BoxType>(data);
  const [isError, setIsError] = useState<boolean>(true);
  const { weightUnit } = ratesInput;

  const { id, length, height, width, weight } = initials;
  const dimUnit = LENGTH_UNITS[weightUnit]?.value || 'cm';
  const { validationStates, validate } = useAiValidation();
  const fieldKey = `box.shipmentPackage.${id}`;

  const updatedBoxes = useMemo(() => {
    const boxIndex = parseInt(id);
    const copy = [...boxes];
    if (!isNaN(boxIndex) && copy[boxIndex]) {
      copy[boxIndex] = {
        ...copy[boxIndex],
        ...initials,
        length: initials.length === '' ? 0 : Number(initials.length),
        width: initials.width === '' ? 0 : Number(initials.width),
        height: initials.height === '' ? 0 : Number(initials.height),
        weight: initials.weight === '' ? 0 : Number(initials.weight),
      };
    }
    return copy;
  }, [boxes, initials, id]);

  const packagePayload = useMemo(() => {
    const l = Number(initials.length || 0);
    const w = Number(initials.width || 0);
    const h = Number(initials.height || 0);
    const wt = Number(initials.weight || 0);

    const isCurrentBoxComplete = l > 0 && w > 0 && h > 0 && wt > 0;
    if (!isCurrentBoxComplete) return null;

    const unitOfMeasurement = weightUnit === 'lb' ? 'lb-in' : 'kg-cm';
    const currentBoxIdx = parseInt(id);

    const boxPayload = updatedBoxes
      .filter((b: any, index: number) => {
        if (index === currentBoxIdx) return true;
        return (
          Number(b.length || 0) > 0 &&
          Number(b.width || 0) > 0 &&
          Number(b.height || 0) > 0 &&
          Number(b.weight || 0) > 0
        );
      })
      .map((b: any) => ({
        length: Number(b.length || 0),
        width: Number(b.width || 0),
        height: Number(b.height || 0),
        weight: Number(b.weight || 0),
        products: (b.products || []).map((p: any) => ({
          description: String(p.description || '').trim(),
          quantity: Number(p.quantity || 0),
          unitValue: Number(p.unitValue || 0),
          HSNName: p.HSNName ? String(p.HSNName).trim() : '',
          HSNCode: p.HSNCode ? String(p.HSNCode).trim() : '',
        })),
      }));

    return {
      purposeOfShipment: (ratesInput as any)?.purposeOfShipment || 'FOR_SALE',
      insurance: typeof (ratesInput as any)?.insurance === 'boolean' ? (ratesInput as any).insurance : false,
      unitOfMeasurement,
      currency: (ratesInput as any)?.currency || 'INR',
      box: boxPayload,
    };
  }, [updatedBoxes, weightUnit, ratesInput, initials.length, initials.width, initials.height, initials.weight, id]);

  useEffect(() => {
    validate(fieldKey, 'shipmentPackage', packagePayload);
  }, [packagePayload, validate, fieldKey]);

  useEffect(() => {
    setInitials({ ...data });
  }, [data]);

  useEffect(() => {
    const error = validateData();
    if (error) setIsError(true);
    else setIsError(false);
  }, [initials]);

  const volumetricWeight = useMemo(() => {
    const l = parseFloat(String(length)) || 0;
    const w = parseFloat(String(width)) || 0;
    const h = parseFloat(String(height)) || 0;
    if (l > 0 && w > 0 && h > 0) {
      return calculateVolume(l, w, h, dimUnit as any);
    }
    return 0;
  }, [length, width, height, dimUnit]);

  const actualWeight = parseFloat(String(weight)) || 0;
  const chargeableWeight = volumetricWeight > actualWeight ? volumetricWeight : actualWeight;

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    const name = event.target.name as keyof BoxType;
    const { value } = event.target;
    const boxTemp = { ...initials };
    if (name === 'length' || name === 'width' || name === 'height' || name === 'weight') {
      boxTemp[name] = value === '' ? '' : value;
    }
    setInitials(boxTemp);
  }

  function handleSubmit() {
    if (isError) return;
    setIsError(false);
    const boxesTemp = [...boxes];
    const index = parseInt(id);
    if (!isNaN(index) && boxesTemp[index]) {
      boxesTemp[index] = {
        ...boxesTemp[index],
        ...initials,
        length: initials.length === '' ? '' : parseFloat(String(initials.length)) || 0,
        width: initials.width === '' ? '' : parseFloat(String(initials.width)) || 0,
        height: initials.height === '' ? '' : parseFloat(String(initials.height)) || 0,
        weight: initials.weight === '' ? '' : parseFloat(String(initials.weight)) || 0,
      };
      setBoxes([...boxesTemp]);
    }
    handleCancel();
  }

  function validateData() {
    const result: ValidationResult = boxSchema.validate(initials, { abortEarly: false });
    const { error } = result;
    return error;
  }

  function handleCancel() {
    setBoxState('details');
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.875rem' }}>
            Edit Box {parseInt(id) + 1}
          </Typography>
          <PackageAiValidationAlert validationState={validationStates[fieldKey]} />
        </Box>
        <Box
          sx={{
            fontSize: '0.7rem',
            fontWeight: 600,
            px: 1,
            py: 0.25,
            borderRadius: '6px',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
          }}
        >
          Box {parseInt(id) + 1} of {boxes.length}
        </Box>
      </Box>

      {/* Weight Section */}
      <Box
        sx={{
          bgcolor: alpha(theme.palette.grey[500], 0.04),
          p: 1.25,
          borderRadius: '8px',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
          <ScaleIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              color: 'text.secondary',
              fontSize: '0.7rem',
            }}
          >
            Package Weight
          </Typography>
        </Box>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <SbTextField
              placeholder="0.00"
              name="weight"
              value={weight}
              type="number"
              onChange={handleChange}
              endAdornment={
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', px: 0.5 }}>
                  {weightUnit}
                </Typography>
              }
            />
          </Grid>
        </Grid>
      </Box>

      {/* Dimensions Section */}
      <Box
        sx={{
          bgcolor: alpha(theme.palette.grey[500], 0.04),
          p: 1.25,
          borderRadius: '8px',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <StraightenIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                color: 'text.secondary',
                fontSize: '0.7rem',
              }}
            >
              Dimensions (L × W × H)
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.7rem' }}>
            Unit: {dimUnit}
          </Typography>
        </Box>
        <Grid container spacing={1}>
          <Grid item xs={4}>
            <SbTextField
              placeholder="Length"
              name="length"
              type="number"
              value={length}
              onChange={handleChange}
              endAdornment={
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                  {dimUnit}
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={4}>
            <SbTextField
              placeholder="Width"
              name="width"
              type="number"
              value={width}
              onChange={handleChange}
              endAdornment={
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                  {dimUnit}
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={4}>
            <SbTextField
              placeholder="Height"
              name="height"
              type="number"
              value={height}
              onChange={handleChange}
              endAdornment={
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.65rem' }}>
                  {dimUnit}
                </Typography>
              }
            />
          </Grid>
        </Grid>
      </Box>

      {/* Live Weight Indicator */}
      {(volumetricWeight > 0 || actualWeight > 0) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 1.25,
            py: 0.75,
            borderRadius: '6px',
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            border: '1px dashed',
            borderColor: alpha(theme.palette.primary.main, 0.2),
          }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
            Volumetric:{' '}
            <strong>
              {volumetricWeight} {weightUnit}
            </strong>
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.primary.main, fontWeight: 700, fontSize: '0.72rem' }}
          >
            Chargeable: {chargeableWeight} {weightUnit}
          </Typography>
        </Box>
      )}

      {/* Actions */}
      <Actions onConfirm={handleSubmit} onCancel={handleCancel} disabled={isError} />
    </Box>
  );
}

export default BoxEdit;
